import React from "react"
import styled, { css } from "styled-components"
import colors from "@constants/colors"
import { timings, cubicBezier } from "@constants/animation"
import { body, bodyBold } from "@constants/typography"
import { Chevron } from "@components/icons"
import DatoContent from "@dato/DatoContent"
import { Content as MaxWidthContainerContent } from "@components/Layout/MaxWidthContainer"

const baseRulerStyles = css`
  ${body}
  flex-wrap: wrap;
  visibility: hidden;
`

const htmlContentStyles = css`
  p {
    margin: 0 0 24px 0;
    padding: 0;
  }

  a {
    text-decoration-line: underline;
  }

  img {
    width: 100%;
    height: auto;
    object-fit: contain;
    object-position: center center;
  }
`

const Container = styled.div`
  border-bottom: solid 1px ${colors.divider};

  .accordionRuler {
    ${baseRulerStyles}
  }

  .htmlAccordionRuler {
    ${baseRulerStyles}
    ${htmlContentStyles}
  }
`

const Label = styled.button<{ expanded: boolean }>`
  ${({ expanded }) => css`
    ${bodyBold}
    align-items: center;
    justify-content: space-between;
    display: flex;
    transition: color ${timings.quick} ${cubicBezier};
    width: 100%;
    height: 72px;
    margin: 0;
    padding: 0;
    border: none;
    background-color: transparent;
    color: ${expanded ? colors.obsidian : colors.slateDark};

    &:hover {
      cursor: pointer;
    }

    &:focus,
    &:active {
      outline: none;
    }
  `}
`

const IconWrapper = styled.div<{ rotate?: number }>`
  ${({ rotate }) => css`
    flex-shrink: 0;
    transform: ${rotate
      ? `translateX(7px) rotate(${rotate}deg)`
      : "translateX(7px)"};
    transition: transform ${timings.quick} ${cubicBezier};
    width: 24px;
    height: 24px;
  `}
`

const Content = styled.div<{
  expanded: boolean
  html?: boolean
}>`
  ${({ expanded, html }) => css`
    ${body}
    position: relative;
    flex-direction: column;
    display: ${expanded ? "flex" : "none"};
    padding-bottom: 24px;
    color: ${colors.slateDark};

    ${html && htmlContentStyles}

    ${MaxWidthContainerContent} {
      padding: 0;
    }
  `}
`

interface AccordionProps {
  label: string
  content?: string
  advancedContent?: any
  html?: boolean
  children?: any
}

const Accordion: React.FC<AccordionProps> = ({
  label,
  content,
  advancedContent,
  html,
  children,
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const [expanded, setExpanded] = React.useState<boolean>(false)

  const toggleExpanded = () => {
    setExpanded(!expanded)
  }

  return (
    <Container ref={containerRef}>
      <Label expanded={expanded} onClick={toggleExpanded}>
        {label}
        <IconWrapper rotate={expanded ? 180 : 0}>
          <Chevron />
        </IconWrapper>
      </Label>
      {advancedContent?.length > 0 ? (
        <Content expanded={expanded}>
          <DatoContent content={advancedContent} />
        </Content>
      ) : html ? (
        <Content
          html
          expanded={expanded}
          dangerouslySetInnerHTML={{ __html: content || "" }}
        />
      ) : children ? (
        <Content expanded={expanded}>{children}</Content>
      ) : (
        <Content expanded={expanded}>{content}</Content>
      )}
    </Container>
  )
}

export default Accordion
